// components/landing/sections/BetaGateCard.tsx
// ---------------------------------------------------------------------------
// Replaces the SSO login panel when the user is not yet granted platform
// access. Auto-registers beta interest on mount, shows a countdown to
// the target launch date, and a success state once recorded.
// ---------------------------------------------------------------------------

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { alpha, Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../../i18n";
import api from "../../../api/api";
import { semanticColors } from "theme/palette";

const TARGET_LAUNCH = new Date("2026-04-14T00:00:00");
const BETA_LOCAL_KEY = "oncocraft_beta_joined";

function getDaysRemaining(): number {
  const diff = TARGET_LAUNCH.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86_400_000));
}

export default function BetaGateCard() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [joined, setJoined] = useState(() => !!localStorage.getItem(BETA_LOCAL_KEY));
  const [submitting, setSubmitting] = useState(false);
  const submitted = useRef(false);

  const targetDays = getDaysRemaining();
  const [displayDays, setDisplayDays] = useState(0);

  // Count-up animation for countdown
  useEffect(() => {
    if (targetDays === 0) {
      setDisplayDays(0);
      return;
    }
    let current = 0;
    const step = Math.max(1, Math.ceil(targetDays / 35));
    const interval = setInterval(() => {
      current += step;
      if (current >= targetDays) {
        setDisplayDays(targetDays);
        clearInterval(interval);
      } else {
        setDisplayDays(current);
      }
    }, 40);
    return () => clearInterval(interval);
  }, [targetDays]);

  // Auto-submit on mount (fire-and-forget, once per session)
  useEffect(() => {
    if (joined || submitted.current) return;
    submitted.current = true;
    setSubmitting(true);

    (async () => {
      try {
        await api.post("/billing/legal/beta-interest/", { product: "oncocraft" });
      } catch {
        // Optimistic UX — show success regardless
      }
      localStorage.setItem(BETA_LOCAL_KEY, "1");
      setJoined(true);
      setSubmitting(false);
    })();
  }, [joined]);

  if (joined) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
          gap: 1,
        }}
      >
        <CheckCircleOutlineIcon sx={{ color: semanticColors.success.main, fontSize: "2.8rem", mb: 1 }} />
        <Typography sx={{ color: theme.palette.text.primary, fontWeight: 700, fontSize: "1rem" }}>
          {t("betaGate.onTheList", "You're on the list.")}
        </Typography>
        <Typography sx={{ color: alpha(theme.palette.text.secondary, 0.7), fontSize: "0.8rem" }}>
          {t("betaGate.weWillReachOut", "We'll reach out when Athena OncoCraft launches.")}
        </Typography>
        <Box
          component="a"
          href="mailto:support@athenatechai.com"
          sx={{
            color: theme.palette.primary.main,
            fontSize: "0.78rem",
            textDecoration: "underline",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mt: 1,
            cursor: "pointer",
          }}
        >
          {t("betaGate.contactUs", "Contact us at support@athenatechai.com")}
          <EmailOutlinedIcon sx={{ fontSize: "0.82rem" }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        gap: 2,
      }}
    >
      {/* "A" watermark (top-right) */}
      <Box
        component="img"
        src="/brand/icon/a_white_path.svg"
        alt=""
        sx={{
          position: "absolute",
          top: 16,
          right: 18,
          height: 26,
          width: "auto",
          opacity: 0.12,
          pointerEvents: "none",
        }}
      />

      <Box>
        <Typography
          sx={{
            fontSize: "0.63rem",
            letterSpacing: "0.14em",
            fontWeight: 700,
            color: theme.palette.primary.light,
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          Athena OncoCraft
        </Typography>

        <Typography
          sx={{
            fontSize: "1.05rem",
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            mb: 1.25,
          }}
        >
          {t("betaGate.headline", "No more manual entry.")}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.8rem",
            color: alpha(theme.palette.text.secondary, 0.78),
            lineHeight: 1.6,
            mb: 1.25,
          }}
        >
          {t(
            "betaGate.body",
            "Every day, clinicians handle pathology reports, lab results, voice notes, spreadsheets. We are building something that reads them all and turns them into structured, queryable data. Automatically. Across 44 cancer types."
          )}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.75rem",
            color: alpha(theme.palette.text.secondary, 0.65),
            fontStyle: "italic",
            lineHeight: 1.5,
          }}
        >
          {t("betaGate.weWillReachOutReady", "We'll reach out when it's ready.")}
        </Typography>
      </Box>

      <Box>
        {submitting && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 1,
            fontSize: "0.7rem",
            color: alpha(theme.palette.text.secondary, 0.5),
          }}
        >
          <span>{t("betaGate.betaOpensIn", "Beta opens in")}</span>
          <Box
            component="span"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              color: alpha(theme.palette.text.secondary, 0.72),
              fontWeight: 700,
              minWidth: 28,
              display: "inline-block",
            }}
          >
            {displayDays}
          </Box>
          <span>{t("betaGate.days", "days")}</span>
        </Box>
      </Box>
    </Box>
  );
}
